// @wc-ignore-file
import type { Project } from "@shared/types/supabase.types";
import { redirect } from "@tanstack/react-router";

import {
  INCOGNITO_SEARCH_VALUE,
  isIncognitoProjectSubpathAllowed,
} from "@/features/superuser/incognito/incognito-route";
import type { WorkspaceAccess } from "@/features/workspaces/workspace-access";
import { type ProjectFeatureFlag, isProjectFeatureEnabled } from "./project-features";

/**
 * CR-144: where the bare project root (`/workspaces/$slug/projects/$projectSlug`)
 * lands. The first enabled module in this order wins; Documents has no toggle,
 * so it is the unconditional last resort. Team Messages is deliberately not a
 * destination (CR-144 Q2).
 *
 * Every "go to this project" entry point (combobox, workspace list, search,
 * notifications, invites, sidebar fallbacks) navigates to the bare root, and the
 * root route's `beforeLoad` redirects through this resolver — keep the order in
 * this one place.
 */
export const PROJECT_HOME_DESTINATIONS = ["change-requests", "tasks", "chats", "docs"] as const;

export type ProjectHomeDestination = (typeof PROJECT_HOME_DESTINATIONS)[number];

// The toggled modules, in fallback order; Documents follows unconditionally.
const TOGGLED_DESTINATIONS: ReadonlyArray<readonly [ProjectHomeDestination, ProjectFeatureFlag]> = [
  ["change-requests", "show_change_requests"],
  ["tasks", "show_tasks"],
  ["chats", "show_ai_chats"],
];

export const PROJECT_HOME_ROUTES = {
  "change-requests": "/workspaces/$slug/projects/$projectSlug/change-requests",
  tasks: "/workspaces/$slug/projects/$projectSlug/tasks",
  chats: "/workspaces/$slug/projects/$projectSlug/chats",
  docs: "/workspaces/$slug/projects/$projectSlug/docs",
} as const satisfies Record<ProjectHomeDestination, string>;

export type ProjectHomeRoute = (typeof PROJECT_HOME_ROUTES)[ProjectHomeDestination];

/**
 * Fail-open like `isProjectFeatureEnabled`: a null project or a missing flag
 * counts as enabled, so an unknown project lands on Change Requests.
 * `isReachable` narrows the candidates further (CR-165: an incognito view only
 * lands on a page that has a read-only version); Documents stays the last
 * resort either way.
 */
export function resolveProjectHomeDestination(
  project: Pick<Project, ProjectFeatureFlag> | null | undefined,
  isReachable: (destination: ProjectHomeDestination) => boolean = () => true,
): ProjectHomeDestination {
  for (const [destination, flag] of TOGGLED_DESTINATIONS) {
    if (isProjectFeatureEnabled(project, flag) && isReachable(destination)) {
      return destination;
    }
  }
  return "docs";
}

/** The TanStack `to` path of the project's resolved home module. */
export function projectHomeRoute(
  project: Pick<Project, ProjectFeatureFlag> | null | undefined,
  isReachable?: (destination: ProjectHomeDestination) => boolean,
): ProjectHomeRoute {
  return PROJECT_HOME_ROUTES[resolveProjectHomeDestination(project, isReachable)];
}

/**
 * The redirect the project root and the module guards throw: the project's
 * resolved home, replacing the current entry so Back does not bounce. A guard
 * can never be sent to itself because only enabled modules are returned.
 *
 * CR-165: in incognito mode the redirect keeps the `?incognito=support` flag
 * (without it the member load path runs) and only lands on an allowlisted
 * page, so the incognito guard never bounces it back to the root in a loop.
 * The member redirect is unchanged: no search.
 */
export function projectHomeRedirect(
  project: Pick<Project, ProjectFeatureFlag> | null | undefined,
  params: { slug: string; projectSlug: string },
  access?: WorkspaceAccess,
) {
  const projectParams = { slug: params.slug, projectSlug: params.projectSlug };
  if (access?.mode === "incognito") {
    return redirect({
      to: projectHomeRoute(project, isIncognitoProjectSubpathAllowed),
      params: projectParams,
      search: { incognito: INCOGNITO_SEARCH_VALUE },
      replace: true,
    });
  }
  return redirect({
    to: projectHomeRoute(project),
    params: projectParams,
    replace: true,
  });
}
